// Daten der Fahrt (B24, Erste Fassung): durchfahrt.json (Ebenen, Orte, Atlas), fahrt-spiel.json (Weg, Wetter je
// Sturmstufe, Anlegestellen, Durchlass) und boot/boote.json (leere Boote mit Plätzen für die Posten), alle vom
// Grafik-Lead in public/art/. Hier wird nur gelesen; Bilder werden einmal geladen und dann scharf gezeichnet.
import type { Strecke, Takt } from './path.ts';
import type { Grenzen, Schaukeln } from './rock.ts';

type Part = [number, number, number, number];
export type Ort = {
  id: string;
  zeit: string;
  name: string;
  himmel: string;
  bootTon: string;
  lichter: number;
  wolkenY: number;
  /** Bildzeile des Horizonts */
  hor?: number;
  wetter: { truebe: number; regen: number };
};
export type Durchfahrt = {
  breite: number;
  hoehe: number;
  rand: number;
  wl: number;
  tempoVorn: number;
  takt: Takt;
  ebenen: { id: string; tempo: number; band: number; sturmTempo: number }[];
  biome: Record<string, { name: string; orte: Ort[]; kulisse: { datei: string; w: number; mitte: number } }>;
  zeilen: Record<string, [number, number]>;
  atlas: Record<string, { datei: string; teile: Record<string, Part> }>;
};

/** Wetter einer Sturmstufe (fahrt-spiel.json, sturmstufen) */
export type Wetter = {
  sturm: number;
  dunkel: number;
  nebel: number;
  regen: number;
  blitz: number;
  wind: number;
  blaetter: number;
  wolken: number;
  baenke: number[];
  baenkeDunkel: number[];
  // Schlussrunde des Grafik-Leads (Fabi 20.10.: ab Nah rauer, Eingeholt schlimmer als Dicht); fehlen sie, bleibt alles wie bisher
  /** Seegang, 0 bis 1 (ab Nah) */
  see?: number;
  /** Rauheit des Wassers (nur in der Stil-Werkbank; der Seegang rechnet mit see) */
  rauheit?: number;
  /** Dunst über dem Wasser am Horizont */
  dunst?: number;
  /** tiefe, dunkle Wolkendecke */
  decke?: number;
  /** so viele Pixel liegen die Wolkenbänke tiefer */
  tief?: number;
  /** grauer Schleier über der Ferne */
  fern?: number;
  /** so viele Pixel länger und schräger fällt der Regen */
  schraeg?: number;
  schaukeln?: Schaukeln;
};

/** Seegang (fahrt-spiel.json, seegang; Rechnung in den Hinweisen „Seegang“) */
export type Seegang = {
  /** je Wellenzug [Richtung in Grad, Länge als Anteil von laenge, Höhe, Phase] */
  zuege: [number, number, number, number][];
  laenge: number;
  dv: number;
  massstab: [number, number, number];
  takt: number;
  stufen: { tiefesTal: number; tal: number; ruecken: number; kamm: number };
  farben: {
    tiefesTal: [number, number];
    tal: [number, number];
    ruecken: [number, number];
    kamm: [number, number];
    schaum: [number, number, number];
    dunkel: [number, number, number];
    himmelGrau: [number, number, number];
  };
};

export type Anlegestelle = {
  hinten: string;
  vorn: string;
  hintenSturm: string;
  vornSturm: string;
  groesse: [number, number];
  y: number;
  wl: number;
  anker: [number, number];
  sturmTempo: number;
  lampen: [number, number][];
  zeichen: [number, number];
};
export type Durchlass = {
  hinten: { datei: string; sturm: string; groesse: [number, number]; y: number };
  vorn: { datei: string; sturm: string; groesse: [number, number]; y: number };
  anker: [number, number];
  sturmTempo: number;
  lampen: [number, number][];
  zeichen: [number, number];
};

export type FahrtSpiel = {
  spiel: {
    strecke: Strecke;
    vorspulen: number;
    durchfahrt: { tempo: number; nachtAb: number; ablegen: number };
    nacht: { farbe: string; deckkraft: number; halten: number; aufhellen: number };
    liegen: { kraeusel: number };
    sturmWechsel: number;
    morgendunst?: { farbe: string; deckkraft: number };
  };
  sturmstufen: Record<string, Wetter>;
  seegang?: Seegang;
  /** Schaukeln: Takt der Phase und die Grenzen der Neigung je Boot */
  schaukeln?: { takt: number; grenzen: Record<string, Grenzen> };
  sauber: Record<string, [number, number]>;
  anlegestelle: Record<string, Anlegestelle>;
  durchlass: Record<string, Durchlass>;
};

/** Ein Platz im Boot (boote.json, plaetze) */
export type Platz = {
  id: string;
  posten: string[];
  posen: Record<string, [number, number]>;
  blick: 'rechts' | 'links';
  sicht: string[];
  z: number;
  raum?: string;
  vor?: string;
};
export type Boot = {
  name: string;
  w: number;
  h: number;
  wl: number;
  heck: number;
  bug: number;
  /** [links, oben] in der Fahrt (640 × 360) */
  fahrt: [number, number];
  pinne_ruhe: { datei: string; spalten: number } | null;
  lampen: [number, number][];
  lichterkette?: [number, number][];
  rauch: [number, number] | null;
  rauch_alle?: [number, number][];
  plaetze: Platz[];
  reihenfolge: { koje: string[]; gemeinschaft: string[] };
  dateien: Record<'quer' | 'aussen' | 'innen' | 'quer_vorn' | 'aussen_vorn' | 'fenster', string> & { pinne_ruhe?: string };
};
export type Boote = { wlFahrt: number; boote: Record<string, Boot> };

const ART = './art/';
const images = new Map<string, HTMLImageElement>();

/** Ein Bild aus public/art/ (Pfad relativ dazu), sobald es geladen ist; sonst null */
export function img(path: string): HTMLImageElement | null {
  let i = images.get(path);
  if (!i) {
    i = new Image();
    i.src = ART + path;
    images.set(path, i);
  }
  return i.complete && i.naturalWidth ? i : null;
}

const store: { durchfahrt: Durchfahrt | null; spiel: FahrtSpiel | null; boote: Boote | null } = { durchfahrt: null, spiel: null, boote: null };
let loading = false;

function get<T>(path: string, put: (d: T) => void): void {
  void fetch(ART + path)
    .then((r) => (r.ok ? (r.json() as Promise<T>) : null))
    .then((d) => d && put(d))
    .catch(() => {});
}

/** Alles, was die Fahrt braucht; null, solange etwas fehlt */
export function loadFahrt(): { d: Durchfahrt; spiel: FahrtSpiel; boote: Boote } | null {
  if (!loading && typeof fetch === 'function') {
    loading = true;
    get<Durchfahrt>('fahrt/durchfahrt.json', (d) => (store.durchfahrt = d));
    get<FahrtSpiel>('fahrt/fahrt-spiel.json', (d) => (store.spiel = d));
    get<Boote>('boot/boote.json', (d) => {
      store.boote = d;
      // gleich alle Bilder der Boote laden, damit das Boot im ersten Bild nicht fehlt
      for (const b of Object.values(d.boote)) for (const f of [...Object.values(b.dateien), b.pinne_ruhe?.datei]) if (f) img(`boot/${f}`);
    });
  }
  const { durchfahrt, spiel, boote } = store;
  return durchfahrt && spiel && boote ? { d: durchfahrt, spiel, boote } : null;
}

/** Nur die Durchfahrt (für das Bild auf der Freundeskarte) */
export const loadDurchfahrt = (): Durchfahrt | null => (loadFahrt(), store.durchfahrt);
/** Nur die Boote (für die Ankunft am Meer) */
export const loadBoote = (): Boote | null => (loadFahrt(), store.boote);
